# TransferPassengers.vue
<script setup>
import { computed } from 'vue'

const props = defineProps({
  direction: {
    type: String,
    required: true,
    validator: (value) => ['inbound', 'outbound'].includes(value)
  },
  staff: {
    type: Array,
    required: false,
    default: []
  },
  weekData: {
    type: Array,
    required: false,
    default: () => []
  },
  transferData: {
    type: Object,
    required: false,
    default: null
  },
  transferId: {
    type: String,
    required: true
  },
  dictionary: {
    type: Object,
    default: () => ({
      passengers: 'Passengers'
    })
  }
})

const passengers = computed(() =>
    (props.weekData || []).filter(passenger =>
        passenger.allocPlaces?.some(place =>
            place.transport?.transportType === "transfer" &&
            place.transport?.coach === props.transferId &&
            place.transport?.type === `transport-${props.direction}`
        )
    )
)

const numberOfStaff = computed( () => {
  return props.staff.length
})

const passengerGroups = computed(() => {
  if (!passengers.value?.length) return []

  return Object.values(passengers.value.reduce((groups, passenger) => {
    const brand = passenger.reference.split('-')[0]

    if (!groups[brand]) {
      groups[brand] = {
        brand,
        passengers: []
      }
    }

    groups[brand].passengers.push(passenger)
    return groups
  }, {}))
})

const calculateSpareSeats = () => {
  if (!props.transferData) return "No Data Available"

  // Use effectiveTotalForScanning which handles both detailed and summary pax data
  const totalPassengers = effectiveTotalForScanning.value
  const otaPax = parseInt(props.transferData?.['OTA Pax']) || 0
  const thirdParties = parseInt(props.transferData?.['Third parties']) || 0
  const reps = parseInt(numberOfStaff.value) || 0

  if (props.transferData?.['Seats Booked']) {
    return parseInt(props.transferData['Seats Booked']) -
        (totalPassengers + otaPax + thirdParties + reps)
  }

  return "No Capacity Listed"
}

// Check if we have data to display - either passengers from weekData or transfer metadata
const hasTransferData = computed(() => {
  if (!props.transferData) return false
  // Show if we have any capacity info (Seats Booked) or passengers
  return props.transferData['Seats Booked'] ||
      passengers.value.length > 0 ||
      parseInt(props.transferData?.['OTA Pax']) > 0 ||
      parseInt(props.transferData?.['Third parties']) > 0
})

// Get total pax from extraData format (WAN pax, WLT pax) when detailed breakdown unavailable
const totalPaxFromTransferData = computed(() => {
  if (!props.transferData) return 0
  const wanPax = parseInt(props.transferData['WAN pax']) || 0
  const wltPax = parseInt(props.transferData['WLT pax']) || 0
  return wanPax + wltPax
})

// Use passengers from weekData if available, otherwise use pax count from transfer metadata
const effectiveTotalForScanning = computed(() => {
  if (passengers.value.length > 0) return passengers.value.length
  return totalPaxFromTransferData.value
})
</script>

<template>
  <div v-if="hasTransferData" class="mb-4">
    <strong class="d-block border-bottom pb-2 mb-2">
      {{ direction.charAt(0).toUpperCase() + direction.slice(1) }}
    </strong>

    <div class="ms-2">
      <!-- Show brand breakdown when we have detailed passenger data -->
      <div
          v-for="group in passengerGroups"
          :key="group.brand"
          class="d-flex justify-content-between mb-1"
      >
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ group.brand }} {{ dictionary.passengers }}:</span>
        <span>{{ group.passengers.length }}</span>
      </div>

      <div class="d-flex justify-content-between mb-1 pb-2 border-bottom">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.totalForScanning }}:</span>
        <span>{{ effectiveTotalForScanning }}</span>
      </div>
      <div class="d-flex justify-content-between mb-1">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.otherPassengers }}:</span>
        <span>{{ transferData?.['OTA Pax'] || 0 }}</span>
      </div>

      <div class="d-flex justify-content-between mb-1">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.thirdParties }}:</span>
        <span>{{ transferData?.['Third parties'] || 0 }}</span>
      </div>

      <div class="d-flex justify-content-between mb-1">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.repsAndWelfare }}:</span>
        <span>{{ numberOfStaff }}</span>
      </div>

      <div class="d-flex justify-content-between mb-1">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.spareSeats }}:</span>
        <span>{{ calculateSpareSeats() }}</span>
      </div>

      <div
          v-if="transferData?.['Ski bags']"
          class="d-flex justify-content-between mt-2 pt-2 border-top"
      >
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span>{{ __DICT__.loadTransferView.skiBags }}:</span>
        <span>{{ transferData?.['Ski bags'] }}</span>
      </div>
    </div>
  </div>
  <div v-else class="text-muted small p-2">
    {{ __DICT__.transferInfo.capacitySummaryNotAvailable }}
  </div>
</template>